import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, Chip, Grid, Card, CardContent, Container,
  Stack, Divider, Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExploreIcon from '@mui/icons-material/Explore';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * Public landing page for Career Compass. Shown at `/` to unauthenticated
 * visitors. Authenticated users never see this — they redirect to `/jobs`.
 *
 * All user-visible text is loaded from the i18n catalog (landing.* keys)
 * so the page renders in Portuguese for browsers reporting pt* locales
 * and English for everyone else. The icon array stays in code because
 * icons are language-agnostic — only the labels load from t().
 */
const HOW_IT_WORKS_ICONS = [DescriptionIcon, SearchIcon, AutoFixHighIcon];

const FEATURE_KEYS = [
  'feature_byok',
  'feature_ghost',
  'feature_archetype',
  'feature_gmail',
  'feature_no_invent',
];

export default function Landing() {
  const { t } = useTranslation();

  // Built per-render so a language switch updates copy without remount.
  const HOW_IT_WORKS = HOW_IT_WORKS_ICONS.map((Icon, i) => ({
    Icon,
    title: t(`landing.step${i + 1}_title`),
    body:  t(`landing.step${i + 1}_body`),
  }));

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Minimal header — no Navbar on landing */}
      <Box
        component="header"
        sx={{
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={1}>
            <ExploreIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary.main"
              sx={{ letterSpacing: '-0.2px' }}
            >
              Career Compass
            </Typography>
            <Chip
              label={t('common.beta')}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Box flex={1} />
            <Button
              component={RouterLink}
              to="/login"
              variant="text"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              {t('landing.cta_sign_in')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxWidth="md" sx={{ pt: { xs: 6, sm: 10 }, pb: { xs: 4, sm: 6 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              letterSpacing: '-0.5px',
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 720,
            }}
          >
            {t('landing.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              maxWidth: 600,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.5,
            }}
          >
            {t('landing.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.2 }}
            >
              {t('landing.cta_get_started')}
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size="large"
              sx={{ px: 4, py: 1.2 }}
            >
              {t('landing.cta_sign_in')}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.disabled" pt={1}>
            {t('landing.closed_beta')}
          </Typography>
        </Stack>
      </Container>

      {/* How it works */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, sm: 8 } }}>
        <Stack spacing={1} alignItems="center" mb={{ xs: 4, sm: 6 }}>
          <Typography
            variant="overline"
            color="primary.main"
            fontWeight={600}
            sx={{ letterSpacing: 1 }}
          >
            {t('landing.how_it_works')}
          </Typography>
          <Typography variant="h4" fontWeight={600} textAlign="center">
            {t('landing.how_subtitle')}
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {HOW_IT_WORKS.map(({ Icon, title, body }, i) => (
            <Grid item xs={12} md={4} key={title}>
              <Card variant="outlined" sx={{ height: '100%', p: 1 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      mb: 2,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography
                    variant="overline"
                    color="text.disabled"
                    fontWeight={600}
                    display="block"
                    mb={0.5}
                  >
                    {t('landing.step', { n: i + 1 })}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    {body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Key features strip */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 4, sm: 6 },
        }}
      >
        <Container maxWidth="md">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            justifyContent="center"
            mb={3}
          >
            <VerifiedUserIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="overline" color="success.main" fontWeight={600} letterSpacing={1}>
              {t('landing.features_label')}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            gap={1}
          >
            {FEATURE_KEYS.map((key) => (
              <Chip
                key={key}
                label={t(`landing.${key}`)}
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary',
                  fontWeight: 500,
                  px: 0.5,
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Bottom CTA */}
      <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 8 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Typography variant="h5" fontWeight={600}>
            {t('landing.ready_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            {t('landing.ready_subtitle')}
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ px: 4, py: 1.2 }}
          >
            {t('landing.cta_get_started')}
          </Button>
        </Stack>
      </Container>

      {/* Footer */}
      <Divider />
      <Box component="footer" sx={{ py: 3 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="caption" color="text.disabled">
              {t('landing.footer_copyright', { year: new Date().getFullYear() })}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Link
                component={RouterLink}
                to="/privacy"
                variant="caption"
                color="text.secondary"
                underline="hover"
              >
                {t('landing.footer_privacy')}
              </Link>
              <Link
                component={RouterLink}
                to="/terms"
                variant="caption"
                color="text.secondary"
                underline="hover"
              >
                {t('landing.footer_terms')}
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
